import type { BasePagingResponse } from "../../lib/api-contract";
import { convertDateToUTCRange } from "../../lib/date-time";
import { apiUtils } from "../../utils/apiUtils";

export interface ScannerEvent {
  id: string;
  title: string;
  location: string;
  start_time: string;
  end_time: string;
}

export interface ScannedAttendeeItem {
  name: string;
  email: string;
  checked_in_at: string;
}

interface EventListItem {
  id: string | number;
  title: string;
  location?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}

const eventBasePath = "/v1/portal/event";

const getTodayDateString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

export const qrScannerApi = {
  async getTodayEvents(signal?: AbortSignal): Promise<ScannerEvent[]> {
    const today = getTodayDateString();
    const response = await apiUtils.getData<BasePagingResponse<EventListItem>>(
      eventBasePath,
      "Failed to load events",
      {
        params: {
          event_date_from: convertDateToUTCRange(today, true),
          event_date_to: convertDateToUTCRange(today, false),
          index: 0,
          size: 100,
          sort_key: "event_date",
          sort_direction: "asc",
        },
        paramsSerializer: { indexes: null },
        signal,
      },
    );

    return (response.list ?? []).map((event) => ({
      id: String(event.id),
      title: event.title,
      location: event.location ?? "",
      start_time: event.start_time ?? "",
      end_time: event.end_time ?? "",
    }));
  },

  async getScannedAttendees(eventId: string, signal?: AbortSignal): Promise<ScannedAttendeeItem[]> {
    const data = await apiUtils.getData<BasePagingResponse<ScannedAttendeeItem>>(
      `${eventBasePath}/${eventId}/scanned-attendees`,
      "Failed to load attendees",
      { params: { index: 0, size: 100 }, signal },
    );
    return [...(data.list ?? [])];
  },

  checkInByQrCode(eventId: string, qrToken: string): Promise<ScannedAttendeeItem> {
    return apiUtils.postData<ScannedAttendeeItem>(
      `${eventBasePath}/check-in-by-qr-code`,
      { event_id: eventId, qr_token: qrToken, check_in_time_utc: new Date().toISOString() },
      "Failed to check in. Please try again.",
    );
  },
};

export default qrScannerApi;
